<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import Column from 'primevue/column';
import { BookTemplate, Pencil, Plus, Search, Trash2 } from '@lucide/vue';
import type { PaginationQuery, TemplateListItem } from '@evolfin/shared';
import { useAuthStore } from '@/stores/auth';
import { useAvvisi } from '@/composables/useAvvisi';
import { useConferma } from '@/composables/useConferma';
import { useListaQuery } from '@/composables/useListaQuery';
import { EvolfinApiError } from '@/api/client';
import TabellaDati from '@/components/ui/TabellaDati.vue';
import PulsanteAzione from '@/components/ui/PulsanteAzione.vue';
import { formattaData } from '@/utils/formato';
import { eliminaTemplate, listaTemplate, type QueryTemplate } from '../api';

definePage({ meta: { titolo: 'Template', privilegi: ['can_manage_templates'] } });

const router = useRouter();
const auth = useAuthStore();
const avvisi = useAvvisi();
const conferma = useConferma();

const puoGestire = computed(() => auth.ha('can_manage_templates'));

const lista = useListaQuery<TemplateListItem>(
  'template',
  (q) => listaTemplate(q as QueryTemplate),
  { iniziale: { sort: 'nome' } as unknown as Partial<PaginationQuery> },
);
const { righe, totale, query, caricamento, onPage, onSort, cerca } = lista;

const testoRicerca = ref('');

const filtroAttivo = computed<string>({
  get: () => {
    const v = (query as QueryTemplate).attivo;
    return v === undefined ? '' : String(v);
  },
  set: (v) => {
    (query as QueryTemplate).attivo = v === '' ? undefined : v === 'true';
    query.page = 1;
  },
});

function apriCreazione(): void {
  void router.push('/template/nuovo');
}

function vaiAlDettaglio(t: TemplateListItem): void {
  void router.push(`/template/${t.id}`);
}

async function elimina(t: TemplateListItem): Promise<void> {
  if (
    !(await conferma({
      titolo: 'Elimina template',
      messaggio: `Eliminare "${t.nome}"? I progetti già nati da questo template restano intatti.`,
      etichettaConferma: 'Elimina',
      distruttiva: true,
    }))
  ) {
    return;
  }
  try {
    await eliminaTemplate(t.id);
    avvisi.successo('Template eliminato');
    await lista.ricarica();
  } catch (errore) {
    const messaggio =
      errore instanceof EvolfinApiError ? errore.message : 'Operazione non riuscita';
    avvisi.errore('Impossibile eliminare', messaggio);
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <header class="flex flex-col items-stretch gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p class="text-xs font-semibold tracking-[0.08em] text-ink-muted uppercase">
          Configurazione
        </p>
        <h1 class="mt-1 font-display text-[33px] leading-[1.07] font-semibold">Template</h1>
        <p class="mt-1 hidden text-[13px] text-ink-secondary sm:block">
          Gli stampi di pratica: fasi tipiche e documenti attesi, copiati alla creazione di un
          progetto.
        </p>
      </div>
      <PulsanteAzione v-if="puoGestire" class="w-full sm:w-auto" @click="apriCreazione">
        Nuovo template
        <template #icona><Plus :size="16" :stroke-width="2" /></template>
      </PulsanteAzione>
    </header>

    <div class="card p-0">
      <div class="flex flex-wrap items-center gap-3 border-b border-line-soft px-4 py-3">
        <label class="relative min-w-[14rem] flex-1">
          <Search
            class="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-muted"
            :stroke-width="2"
            aria-hidden="true"
          />
          <input
            v-model="testoRicerca"
            type="search"
            placeholder="Cerca per nome…"
            class="w-full rounded-sm border border-line bg-surface py-2 pr-3 pl-9 text-[13px] text-ink transition-[border-color] focus:border-[var(--color-accent)]"
            @input="cerca(testoRicerca)"
          />
        </label>

        <select v-model="filtroAttivo" class="filtro" aria-label="Filtra per stato">
          <option value="">Tutti</option>
          <option value="true">Attivi</option>
          <option value="false">Disattivati</option>
        </select>
      </div>

      <TabellaDati
        :righe="righe"
        :totale="totale"
        :query="query"
        :caricamento="caricamento"
        @page="onPage"
        @sort="onSort"
      >
        <Column field="nome" header="Template" :sortable="true" primaria>
          <template #body="{ data }: { data: TemplateListItem }">
            <router-link
              :to="`/template/${data.id}`"
              class="flex items-center gap-2.5 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
            >
              <span
                class="grid size-8 shrink-0 place-items-center rounded-md bg-[var(--surface-accent-soft)] text-[var(--color-primary)]"
                aria-hidden="true"
              >
                <BookTemplate :size="16" :stroke-width="1.9" />
              </span>
              <div class="min-w-0">
                <p class="truncate font-medium text-ink hover:text-[var(--color-primary)]">
                  {{ data.nome }}
                </p>
                <p v-if="!data.attivo" class="truncate text-[12px] text-ink-muted">Disattivato</p>
              </div>
            </router-link>
          </template>
        </Column>

        <Column header="Fasi">
          <template #body="{ data }: { data: TemplateListItem }">
            <span class="text-[13px] text-ink-secondary tabular-nums">{{ data.nFasi }}</span>
          </template>
        </Column>

        <Column field="creatoIl" header="Creato il" :sortable="true">
          <template #body="{ data }: { data: TemplateListItem }">
            <span class="text-[12.5px] text-ink-secondary tabular-nums">
              {{ formattaData(data.creatoIl) }}
            </span>
          </template>
        </Column>

        <Column header="" azioni :style="{ width: '6rem' }">
          <template #body="{ data }: { data: TemplateListItem }">
            <div class="flex items-center justify-end gap-1" @click.stop>
              <button
                type="button"
                class="rounded-sm p-1.5 text-ink-muted transition-colors hover:bg-soft hover:text-ink"
                title="Apri scheda"
                @click="vaiAlDettaglio(data)"
              >
                <Pencil :size="16" :stroke-width="1.8" />
              </button>
              <button
                v-if="puoGestire"
                type="button"
                class="rounded-sm p-1.5 text-ink-muted transition-colors hover:bg-soft hover:text-[var(--color-danger-text)]"
                title="Elimina"
                @click="elimina(data)"
              >
                <Trash2 :size="16" :stroke-width="1.8" />
              </button>
            </div>
          </template>
        </Column>

        <template #vuoto>
          <p class="py-10 text-center text-[13px] text-ink-secondary">Nessun template trovato.</p>
        </template>
      </TabellaDati>
    </div>
  </div>
</template>

<style scoped>
.filtro {
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
  background-color: var(--color-surface);
  padding-block: 0.5rem;
  padding-inline-start: 0.65rem;
  font-size: 13px;
  color: var(--color-text);
  transition: border-color 120ms ease;
}
.filtro:focus {
  outline: none;
  border-color: var(--color-accent);
}
</style>
